import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Check, Star, Shield, ArrowRight, Sparkles, Loader2, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Packages = () => {
  const [loadingPackage, setLoadingPackage] = useState(null);

  const packages = [
    {
      id: 'starter',
      name: 'Career Foundations',
      price: '$597',
      priceNum: 597,
      tagline: 'Get your foundations right.',
      whoFor: 'You are applying but not getting callbacks. Your CV and LinkedIn are not working and you do not know why.',
      features: [
        '1 x Role Clarity session (60 min): identify 1-2 roles that genuinely fit your skills and experience',
        '1 x CV review and rewrite session (60 min): line-by-line feedback, suggestions, and a rewrite template you own',
        '1 x LinkedIn profile session (45 min): headline, summary, and visibility fixes',
        'Email support for 2 weeks after sessions'
      ],
      duration: '3 sessions across 2-3 weeks',
      bestFor: 'Graduates and early-career professionals who need to fix the basics before anything else',
      cta: 'Add to Cart',
      popular: false
    },
    {
      id: 'full-bundle',
      name: 'Career Accelerator',
      price: '$1,297',
      priceNum: 1297,
      tagline: 'Accelerate your job search, end to end.',
      whoFor: 'You want everything: profile, strategy, and interview prep in one place.',
      features: [
        'Everything in Career Foundations (Role Clarity, CV, LinkedIn)',
        '1 x Job Hunt Strategy session (60 min): a 6-8 week search plan built around your situation',
        '1 x Interview Prep session (60 min): STAR framework, common AU interview questions, behavioural answers',
        'Email support for 4 weeks after sessions'
      ],
      duration: '5 sessions across 4-5 weeks',
      bestFor: 'Professionals who want a structured, end-to-end approach rather than fixing one thing at a time',
      cta: 'Add to Cart',
      popular: true
    },
    {
      id: 'group-sessions',
      name: 'Group Career Foundations',
      price: '$397',
      priceNum: 397,
      priceNote: 'per person',
      tagline: 'Get your foundations right, together.',
      whoFor: 'You want the same foundations coaching as our 1:1 program, delivered in a small group setting at a lower price.',
      features: [
        'Role Clarity workshop (group)',
        'CV and LinkedIn review workshop (group)',
        'Minimum 3 participants per group'
      ],
      duration: '2 workshops',
      note: 'Groups are kept small (3-5 people maximum) so every session is interactive, not just a webinar.',
      bestFor: 'Professionals who want foundations coaching but prefer a collaborative environment at a lower price point',
      cta: 'Add to Cart',
      popular: false
    },
    {
      id: 'elite',
      name: 'Elite Career Partnership',
      price: '$2,997',
      priceNum: 2997,
      tagline: 'Intensive, personalised, and built around you.',
      whoFor: 'You are serious about landing the right role in the next 60-90 days and want full support doing it.',
      features: [
        'Deep-dive Role Clarity session (90 min)',
        'Full CV and LinkedIn overhaul with multiple rounds of feedback',
        '3 x Interview Prep sessions with detailed personalised feedback',
        '2 x Mock Interview sessions',
        'Full Job Hunt Strategy: a 6-8 week plan with weekly check-ins',
        'Priority email and WhatsApp support for 8 weeks',
        'Unlimited email reviews of applications before you send them'
      ],
      duration: '8-10 weeks',
      bestFor: 'Senior professionals, people on tight visa timelines, or anyone who wants the most intensive level of support',
      cta: 'Book a Free Discovery Call',
      popular: false,
      elite: true
    }
  ];

  const addon = {
    id: 'mock-interview',
    name: 'Interview Rehearsal Session',
    price: '$149',
    priceNum: 149,
    description: 'Available as a standalone session or add-on to any package.',
    details: 'One 45-minute mock interview over video call with written feedback delivered within 24 hours. Covers question technique, structure, delivery, and Australian interview norms.'
  };

  const handleCheckout = async (packageId, externalLink = null) => {
    // If external link is provided, open it in new tab
    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
      return;
    }
    
    setLoadingPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert(`Checkout error: ${errorMessage}. Please try again.`);
      setLoadingPackage(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-brand-pale/30 to-white">
      <Helmet>
        <title>Packages | Mattoo Consulting</title>
        <meta name="description" content="Career coaching packages for skilled migrants and overseas professionals. From foundations to complete job search systems." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-gradient-to-br from-brand-mist/50 to-brand-pale/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="font-serif text-4xl md:text-5xl font-bold tracking-tight text-brand-dark mb-4">
              Our Packages
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Four options. One free call to figure out which one is right for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-3xl border-2 p-8 transition-all duration-300 hover:shadow-2xl ${
                  pkg.popular
                    ? 'border-brand-primary shadow-xl scale-105'
                    : pkg.elite
                    ? 'border-brand-sand bg-gradient-to-br from-brand-sand/5 to-transparent'
                    : 'border-brand-pale hover:border-brand-primary/30'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-primary text-white px-6 py-1.5 rounded-full text-sm font-semibold flex items-center gap-2">
                    <Star className="w-4 h-4 fill-current" />
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-serif text-2xl font-bold text-brand-dark mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-brand-medium font-medium mb-4">{pkg.tagline}</p>
                  
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className={`font-serif text-4xl font-bold ${pkg.elite ? 'text-brand-sand' : 'text-brand-primary'}`}>
                      {pkg.price}
                    </span>
                    {pkg.priceNote && (
                      <span className="text-sm text-muted-foreground">{pkg.priceNote}</span>
                    )}
                    <span className="text-sm text-muted-foreground">AUD</span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{pkg.whoFor}</p>
                </div>

                <div className="space-y-4 mb-8">
                  <div>
                    <h4 className="text-sm font-semibold text-brand-dark mb-3">What's included:</h4>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${pkg.elite ? 'text-brand-sand' : 'text-brand-primary'}`} />
                          <span className="text-brand-dark leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-brand-pale">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold">Timeline:</span> {pkg.duration}
                    </p>
                  </div>

                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold">Best for:</span> {pkg.bestFor}
                    </p>
                  </div>

                  {pkg.note && (
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground italic">{pkg.note}</p>
                    </div>
                  )}
                </div>

                {pkg.elite ? (
                  <Link
                    to="/book-a-call"
                    onClick={() => window.scrollTo(0, 0)}
                    className="w-full text-center rounded-full px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 bg-brand-sand hover:bg-brand-sand/90 text-white"
                  >
                    {pkg.cta}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                ) : (
                  <button
                    onClick={() => handleCheckout(pkg.id, pkg.ctaLink)}
                    disabled={loadingPackage === pkg.id}
                    data-testid={`${pkg.id}-checkout-btn`}
                    className={`w-full text-center rounded-full px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 ${
                      pkg.popular
                        ? 'bg-brand-primary hover:bg-brand-medium text-white shadow-lg'
                        : 'bg-brand-dark hover:bg-brand-medium text-white'
                    }`}
                  >
                    {loadingPackage === pkg.id ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        {pkg.cta}
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add-on Section */}
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto mb-16">
            <div className="bg-gradient-to-br from-brand-mist/30 to-brand-pale/20 rounded-2xl border border-brand-pale p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-brand-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-2">
                    <h3 className="font-serif text-xl font-bold text-brand-dark">
                      Add-on: {addon.name}
                    </h3>
                    <span className="font-serif text-2xl font-bold text-brand-primary">
                      {addon.price}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{addon.description}</p>
                  <p className="text-sm text-brand-dark leading-relaxed">{addon.details}</p>
                  
                  <button
                    onClick={() => handleCheckout(addon.id)}
                    disabled={loadingPackage === addon.id}
                    className="mt-4 inline-flex items-center gap-2 bg-white border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-full px-6 py-2 font-medium transition-all"
                  >
                    {loadingPackage === addon.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Add to Cart
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Discovery Call CTA */}
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-brand-primary to-brand-medium rounded-3xl p-10 text-center text-white shadow-2xl">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-90" />
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
                Not sure which one is right?
              </h2>
              <p className="text-lg mb-6 text-white/90 max-w-2xl mx-auto">
                A free 30-minute call. No pitch, no pressure — just an honest look at where you are, what's holding you back, and whether (and which) package would actually help.
              </p>
              <Link
                to="/book-a-call"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 bg-white text-brand-primary hover:bg-brand-pale rounded-full px-8 py-4 text-lg font-semibold transition-all hover:shadow-xl hover:-translate-y-1"
              >
                <Calendar className="w-5 h-5" />
                Book a Free Discovery Call
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
